"use client";

import classNames from "classnames";
import { MdOutlineLanguage } from "react-icons/md";
import { Select, SelectMode } from "@npm_leadtech/qr-ui-components";

import { DEFAULT_LANGUAGE_OPTIONS, LANGUAGE_SELECTOR_DATA_QA } from "./constants";
import type { LanguageOption, LanguageSelectorProps } from "./types";

import "./styles.scss";

const findOptionByValue = (options: LanguageOption[], locale?: string) => {
  if (!locale) {
    return null;
  }

  return options.find((option) => option.value === locale) ?? null;
};

export const LanguageSelectorBase = ({
  id,
  value,
  options = DEFAULT_LANGUAGE_OPTIONS,
  placeholder = "Select language",
  label,
  ariaLabel,
  ariaDescribedBy,
  disabled = false,
  dataQa = LANGUAGE_SELECTOR_DATA_QA,
  className,
  selectClassName,
  fullWidth = false,
  shape = "ghost",
  onChange,
  ...restProps
}: LanguageSelectorProps) => {
  const wrapperClassName = classNames("language-selector", className, {
    "language-selector--fullWidth": fullWidth,
  });

  const selectClassNames = classNames("language-selector__select", selectClassName);

  const handleLanguageChange = (nextValue: string | string[]) => {
    if (Array.isArray(nextValue)) {
      return;
    }

    if (nextValue === value) {
      return;
    }

    const nextLocale = nextValue ?? "";
    const selectedOption = findOptionByValue(options, nextLocale);
    onChange?.(nextLocale, selectedOption);
  };

  const ariaLabelValue = ariaLabel ?? label ?? "Language selector";
  const prefixIcon = shape !== "outlined" ? <MdOutlineLanguage /> : null;

  return (
    <div className={wrapperClassName}>
      <Select
        id={id}
        multiple={false}
        value={value}
        label={label}
        placeholder={placeholder}
        aria-label={ariaLabelValue}
        aria-describedby={ariaDescribedBy}
        shape={shape}
        disabled={disabled}
        options={options}
        onChange={handleLanguageChange}
        dataQA={dataQa}
        className={selectClassNames}
        prefixIcon={prefixIcon}
        mode={SelectMode.radio}
        {...restProps}
      />
    </div>
  );
};
